import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import InboxOperationsPanel from './InboxOperationsPanel';
import LinkedInPlatformHealthPanel from './LinkedInPlatformHealthPanel';
import LinkedInRepairMonitoringPanel from './LinkedInRepairMonitoringPanel';
import SuperAdminMonitoringPanel from './SuperAdminMonitoringPanel';
import ClientRuntimeErrorsPanel from './ClientRuntimeErrorsPanel';

function statusMeta(status) {
  if (status === 'healthy') return { label: 'Gezond', classes: 'bg-green-100 text-green-700' };
  if (status === 'degraded') return { label: 'Hersteld', classes: 'bg-amber-100 text-amber-700' };
  if (status === 'conflict') return { label: 'Conflict', classes: 'bg-red-100 text-red-700' };
  if (status === 'ready') return { label: 'Klaar', classes: 'bg-blue-100 text-blue-700' };
  return { label: 'Nog niet gecontroleerd', classes: 'bg-gray-100 text-gray-600' };
}

function dateLabel(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

export default function Operations() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyCampaignId, setBusyCampaignId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const response = await api.get('/operations/runtime-health');
      setData(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const summary = data?.summary || {};
  const campaigns = useMemo(() => data?.campaigns || [], [data]);

  const reconcile = async (campaignId) => {
    setBusyCampaignId(campaignId);
    setError('');
    setNotice('');
    try {
      const response = await api.post(`/operations/runtime-health/campaigns/${campaignId}/reconcile`);
      const repaired = response.data?.repairedTaskCount || 0;
      setNotice(repaired > 0
        ? `${repaired} ontbrekende workflowtask${repaired === 1 ? '' : 's'} hersteld.`
        : 'Runtime gecontroleerd; geen herstel nodig.');
      await load();
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
      await load();
    } finally {
      setBusyCampaignId(null);
    }
  };

  if (loading) return <main className="p-6 lg:p-8"><div className="rounded-xl border bg-white p-6">Operations laden…</div></main>;

  return (
    <main className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">Leadgenflow V2</p>
          <h1 className="text-2xl font-bold text-gray-900">Operations & runtime health</h1>
          <p className="mt-1 text-sm text-gray-600">Controleert inbox-sync, platform health, campaign runtimes en herstelbare problemen voordat ze outreach blokkeren.</p>
        </div>
        <button onClick={load} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Vernieuwen</button>
      </div>

      {notice && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{notice}</div>}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <SuperAdminMonitoringPanel />
      <ClientRuntimeErrorsPanel />
      <LinkedInRepairMonitoringPanel />
      <LinkedInPlatformHealthPanel />
      <InboxOperationsPanel />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-xl border bg-white p-4"><span className="text-sm text-gray-500">Campagnes</span><strong className="mt-1 block text-2xl">{summary.campaigns || 0}</strong></div>
        <div className="rounded-xl border bg-white p-4"><span className="text-sm text-gray-500">Gezond</span><strong className="mt-1 block text-2xl text-green-700">{summary.healthy || 0}</strong></div>
        <div className="rounded-xl border bg-white p-4"><span className="text-sm text-gray-500">Hersteld</span><strong className="mt-1 block text-2xl text-amber-700">{summary.degraded || 0}</strong></div>
        <div className="rounded-xl border bg-white p-4"><span className="text-sm text-gray-500">Conflicten</span><strong className="mt-1 block text-2xl text-red-700">{summary.conflicts || 0}</strong></div>
        <div className="rounded-xl border bg-white p-4"><span className="text-sm text-gray-500">Ongecontroleerd</span><strong className="mt-1 block text-2xl text-gray-700">{summary.unchecked || 0}</strong></div>
      </section>

      <section className="rounded-xl border bg-white overflow-hidden">
        <div className="border-b px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div><h2 className="font-semibold text-gray-900">Campaign runtimes</h2><p className="text-sm text-gray-500">Health worker: {data?.worker?.enabled ? 'actief' : 'uitgeschakeld'} · interval {Math.round((data?.worker?.intervalMs || 0) / 60000)} min.</p></div>
            <span className="text-xs text-gray-400">Server: {dateLabel(data?.serverTime)}</span>
          </div>
        </div>
        <div className="divide-y">
          {campaigns.length === 0 && <div className="p-6 text-sm text-gray-500">Nog geen campagnes gevonden.</div>}
          {campaigns.map((item) => {
            const manifest = item.manifest || {};
            const meta = statusMeta(manifest.status);
            const expected = manifest.expectedTaskCount || 0;
            const present = manifest.presentTaskCount || 0;
            return (
              <div key={item.campaignId} className="p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{item.title || 'Campagne'}</h3>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${meta.classes}`}>{meta.label}</span>
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-600">{item.campaignStatus || 'Onbekend'}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500">
                      <span>Tasks: <strong className="text-gray-700">{present}/{expected}</strong></span>
                      <span>Laatste check: <strong className="text-gray-700">{dateLabel(manifest.healthCheckedAt || manifest.reconciledAt)}</strong></span>
                      {manifest.repairedTaskCount > 0 && <span>Laatste repair: <strong className="text-amber-700">{manifest.repairedTaskCount}</strong></span>}
                      {manifest.conflictCount > 0 && <span>Conflicten: <strong className="text-red-700">{manifest.conflictCount}</strong></span>}
                    </div>
                    {manifest.healthErrorMessage && <p className="mt-2 text-sm text-red-700">{manifest.healthErrorCode}: {manifest.healthErrorMessage}</p>}
                  </div>
                  <button onClick={() => reconcile(item.campaignId)} disabled={busyCampaignId === item.campaignId} className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                    {busyCampaignId === item.campaignId ? 'Controleren…' : 'Runtime controleren / herstellen'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
